import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PayModal = ({ open, onClose, onSubmit, requestId }) => {
  const [passcode, setPasscode] = useState("");
  const [requiresReceipt, setRequiresReceipt] = useState(false);
  const [passcodeError, setPasscodeError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [vendorName, setVendorName] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [checkDate, setCheckDate] = useState(null);
  const [bank, setBank] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [payTo, setPayTo] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [entityType, setEntityType] = useState("");

  const handleSubmit = () => {
    if (!passcode) {
      setPasscodeError("Passcode is required");
      return;
    }

    if (isNaN(passcode) || passcode.trim() === "") {
      setPasscodeError("Passcode must be a valid number");
      return;
    }

    if (paymentMethod === "Check") {
      if (!checkNumber || checkNumber.trim() === "") {
        setPasscodeError("Check Number is required");
        return;
      }
      if (!checkDate) {
        setPasscodeError("Check Date is required");
        return;
      }
      if (!bank || bank.trim() === "") {
        setPasscodeError("Bank is required");
        return;
      }
      if (!amount || isNaN(amount) || amount <= 0) {
        setPasscodeError("Valid amount is required");
        return;
      }
      if (!payTo || payTo.trim() === "") {
        setPasscodeError("Pay To is required");
        return;
      }
      if (!approvedBy || approvedBy.trim() === "") {
        setPasscodeError("Approved By is required");
        return;
      }
      if (!entityType || entityType.trim() === "") {
        setPasscodeError("Entity Type is required");
        return;
      }
    }

    if (requiresReceipt && (!vendorName || vendorName.trim() === "")) {
      setPasscodeError("Payee Name is required when receipt is required");
      return;
    }

    setPasscodeError("");
    onSubmit({
      requestId,
      paymentCode: parseInt(passcode, 10),
      requiresReceipt,
      vendorName: requiresReceipt ? vendorName : undefined,
      paymentMethod,
      ...(paymentMethod === "Check" && {
        checkNumber,
        checkDate: checkDate ? checkDate.toISOString() : null,
        bank,
        amount: parseFloat(amount),
        currency,
        payTo,
        approvedBy,
        entityType,
      }),
    });

    setPasscode("");
    setRequiresReceipt(false);
    setPaymentMethod("Cash");
    setVendorName("");
    setCheckNumber("");
    setCheckDate(null);
    setBank("");
    setAmount("");
    setCurrency("USD");
    setPayTo("");
    setApprovedBy("");
    setEntityType("");
    setPasscodeError("");
    onClose();
  };

  const handlePasscodeChange = (e) => {
    setPasscode(e.target.value);
    setPasscodeError("");
  };

  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.checked ? "Check" : "Cash");
    setPasscodeError("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Process Payment</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentMethod === "Check"}
                  onChange={handlePaymentMethodChange}
                  color="primary"
                />
              }
              label={`Payment Method: ${paymentMethod}`}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              autoFocus
              margin="dense"
              label="Request Passcode"
              type="text"
              fullWidth
              value={passcode}
              onChange={handlePasscodeChange}
              error={!!passcodeError}
              helperText={passcodeError}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresReceipt}
                  onChange={(e) => setRequiresReceipt(e.target.checked)}
                />
              }
              label="Receipt Required"
            />
          </Grid>
          {requiresReceipt && (
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Vendor Name"
                type="text"
                fullWidth
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                sx={{ mb: 2 }}
              />
            </Grid>
          )}
          {paymentMethod === "Check" && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Check Number"
                  type="text"
                  fullWidth
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  selected={checkDate}
                  onChange={(date) => setCheckDate(date)}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="Select Check Date"
                  customInput={
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Check Date"
                      sx={{ mb: 2 }}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Bank"
                  type="text"
                  fullWidth
                  value={bank}
                  onChange={(e) => setBank(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Amount"
                  type="number"
                  fullWidth
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={currency}
                    label="Currency"
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="LRD">LRD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Pay To"
                  type="text"
                  fullWidth
                  value={payTo}
                  onChange={(e) => setPayTo(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="Approved By"
                  type="text"
                  fullWidth
                  value={approvedBy}
                  onChange={(e) => setApprovedBy(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    value={entityType}
                    label="Entity Type"
                    onChange={(e) => setEntityType(e.target.value)}
                  >
                    <MenuItem value="">Select Entity Type</MenuItem>
                    <MenuItem value="PaymentVoucher">Payment Voucher</MenuItem>
                    <MenuItem value="Loan">Loan</MenuItem>
                    <MenuItem value="SalaryAdvance">Salary Advance</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!passcode}
        >
          Submit Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayModal;
